import React from 'react'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { ArrowLeft, ArrowRight, Images } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { useBlazeRecommendations } from 'components/ImageMenu/ImageMenuModal/BlazeRecommendations/useBlazeRecommendations'
import { GeneratingRecommendation } from 'components/ImageMenu/ImageMenuModal/BlazeRecommendations/GeneratingRecommendation'
import { useAddBrandKitImages } from 'components/AddMoreImagesModal/hooks/useAddBrandKitImages'
import { AddMoreImagesProvider, useAddMoreImagesContext } from 'components/AddMoreImagesModal/AddMoreImagesContext'
import { GeneratingImageDescriptions } from 'components/AddMoreImagesModal/GeneratingImageDescriptions'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { Text } from 'blaze-ui/Text/Text'
import { useModals } from 'almanac-ui/Modal'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import Styles from './AddMoreImagesV2.module.scss'

type Props = {
  onNext: () => void
  onBack: () => void
  brandKitId: number
  onSuccess?: (newMediaFiles: IMediaFile[]) => void
}

const AddMoreImagesV2Content = ({ onNext, onBack, brandKitId, onSuccess }: Props): React.ReactElement => {
  const { selectedImages, toggleSelectedImage } = useAddMoreImagesContext()
  const [isUploading, setIsUploading] = React.useState(false)
  const [imageDescriptionsUpdated, setImageDescriptionsUpdated] = React.useState(false)
  const { openModal, closeAllModals } = useModals()

  const { data: brandKitData } = useBrandKit(brandKitId)
  const imageDescriptions = brandKitData?.brand_kit.brand_visual_kit?.image_descriptions

  const { recommendations, updateRecommendation, removeRecommendation, fetchNextPage, brandProfileId, isGenerating } =
    useBlazeRecommendations()

  const hasImageDescriptions = imageDescriptionsUpdated || (imageDescriptions && imageDescriptions.length > 0)
  const shouldShowLoading = !brandProfileId || !hasImageDescriptions

  const addBrandKitImages = useAddBrandKitImages({
    onNext,
    brandKitId,
    onSuccess,
  })

  const handleAddFromLibrary = () => {
    openModal(ImageMenuModal, {
      onUse: (images: ImageMenuSelectedImage[]) => {
        images.forEach((image) => {
          if (!selectedImages.some((i) => i.url === image.url)) {
            toggleSelectedImage(image)
          }
        })
        closeAllModals()
      },
      maxCount: Infinity,
      title: 'Add images & videos',
      showSearchBar: true,
      hiddenProviders: ['uploads', 'blazeRecommendations', 'brandKits', 'gifs', 'stockVideo'],
      uploadMedia: true,
    })
  }

  const isSelected = React.useCallback(
    (imageUrl: string) => selectedImages.some((i) => i.url === imageUrl),
    [selectedImages],
  )

  const continueButtonText = React.useMemo(() => {
    if (isUploading) return 'Adding to Brand Kit...'
    if (selectedImages.length > 0) return 'Add to Brand Kit'
    return 'Continue'
  }, [isUploading, selectedImages.length])

  const handleContinue = async () => {
    if (selectedImages.length === 0) {
      onNext()
      return
    }

    setIsUploading(true)
    try {
      await addBrandKitImages()
    } finally {
      setIsUploading(false)
    }
  }

  if (shouldShowLoading) {
    return (
      <MultiStepStepLayout
        title="Add images and videos to your Brand Kit"
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
      >
        <GeneratingImageDescriptions
          brandKitId={brandKitId}
          onImageDescriptionsUpdate={() => setImageDescriptionsUpdated(true)}
        />
      </MultiStepStepLayout>
    )
  }

  return (
    <MultiStepStepLayout
      title="Add images and videos to your Brand Kit"
      scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
    >
      <div className={Styles.gridContainer}>
        <div className={Styles.grid}>
          {recommendations.map((rec) => {
            if (rec.type === 'placeholder') {
              return (
                <GeneratingRecommendation
                  key={rec.id}
                  id={rec.id}
                  brandProfileId={brandProfileId}
                  onSuccess={updateRecommendation}
                  onError={removeRecommendation}
                  variant="grid"
                />
              )
            }
            if (!rec.image) return null
            const image = rec.image
            return (
              <div key={rec.id} className={Styles.imageWrapper}>
                <ImageThumbnail
                  image={image}
                  onToggleSelect={() => toggleSelectedImage(image)}
                  isSelected={isSelected(image.url)}
                />
              </div>
            )
          })}
        </div>
        <div className={Styles.generateMore}>
          <Button variant="ghost-tertiary" size="lg" onPress={() => fetchNextPage()} isDisabled={isGenerating}>
            {isGenerating ? 'Generating...' : 'Generate More'}
          </Button>
          <Button variant="ghost-tertiary" size="lg" onPress={handleAddFromLibrary} frontIcon={Images}>
            Add from Library
          </Button>
        </div>
      </div>

      <div className={Styles.floatingFooter}>
        <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft} aria-label="Back">
          Back
        </Button>
        <div className={Styles.selectedSection}>
          {selectedImages.length > 0 && (
            <>
              <Text variant="label">{selectedImages.length} selected</Text>
              <div className={Styles.selectedThumbnails}>
                {selectedImages.slice(0, 5).map((image, index) => (
                  <img key={image.url || index} src={image.url} alt="" className={Styles.selectedThumbnail} />
                ))}
              </div>
            </>
          )}
        </div>
        <Button size="xl" onPress={handleContinue} endIcon={ArrowRight} aria-label="Continue" isDisabled={isUploading}>
          {continueButtonText}
        </Button>
      </div>
    </MultiStepStepLayout>
  )
}

export const AddMoreImagesV2 = (props: Props): React.ReactElement => {
  return (
    <AddMoreImagesProvider>
      <AddMoreImagesV2Content {...props} />
    </AddMoreImagesProvider>
  )
}
